/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import { Box, List, ListItem, useTheme } from "@mui/material";
import { planDescription } from "@/utils/staticData/homeData";
import { borderLineStyles, getItemStyles } from "./styles";
import useResponsive from "@/hooks/useResponsive";
import { animated, useSpring } from "react-spring";

const AnimatedBox = animated(Box);

const DescriptionList = () => {
  const [activeStep, setActiveStep] = useState(0);
  const listRef = useRef(null);
  const timer = useRef<any>(null);

  const theme = useTheme();
  const isMobile = useResponsive("down", "md");
  const listHeight = isMobile ? 156 : 195;
  const lineHeight = isMobile ? 26 : 40;
  const height = listHeight - lineHeight;

  // Watch whether list is in view and start animation
  useEffect(() => {
    const ref = listRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !timer.current) {
          timer.current = setInterval(() => {
            setActiveStep((state) => (state + 1) % planDescription.length);
          }, 1500);
          return () => {
            clearInterval(timer.current);
            setActiveStep(0);
          };
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.5 }
    );

    if (ref) {
      observer.observe(ref);
    }

    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
      clearInterval(timer.current);
      setActiveStep(0);
    };
  }, []);

  //  on visibility change star animation over
  useEffect(() => {
    const onChange = () => {
      if (document.visibilityState === "hidden") {
        // Stop list item animation on visibility change to 'hidden'
        clearInterval(timer.current);
        setActiveStep(0);
        timer.current = null;
      } else {
        // Start list item animation on visibility change to 'visible'
        setActiveStep(0);
        if (!timer.current) {
          timer.current = setInterval(() => {
            setActiveStep((state) => (state + 1) % planDescription.length);
          }, 1500);
        }
      }
    };

    document.addEventListener("visibilitychange", onChange);

    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  // Line animation
  const [{ y: animatedY }, api] = useSpring(() => ({ y: 0 }));

  // Line animation transition based on active step
  useEffect(() => {
    if (activeStep === 0) {
      api.start({ y: 0 });
    }

    if (activeStep === 1) {
      api.start({ y: isMobile ? lineHeight + 8 : lineHeight });
    }

    if (activeStep === 2) {
      api.start({ y: isMobile ? lineHeight * 2 + 12 : lineHeight * 2 });
    }

    if (activeStep === 3) {
      api.start({ y: isMobile ? lineHeight * 3 + 16 : lineHeight * 3 });
    }

    if (activeStep === 4) {
      api.start({ y: isMobile ? lineHeight * 4 + 24 : lineHeight * 4 });
    }
  }, [api, height, activeStep, isMobile, lineHeight]);

  return (
    <Box>
      <List
        disablePadding
        ref={listRef}
        sx={{ mb: { xs: 6.75, md: 0 }, pl: 1 }}
      >
        {planDescription.map((item, index) => (
          <ListItem
            disablePadding
            key={index}
            sx={getItemStyles(activeStep === index)}
          >
            {item}
          </ListItem>
        ))}
      </List>

      <Box
        height={listHeight}
        width={4}
        bgcolor="rgba(217, 217, 217, 0.2)"
        sx={borderLineStyles}
      >
        <AnimatedBox
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "4px",
            height: lineHeight,
            backgroundColor: theme.palette.primary.main,
            transform: animatedY.to((value) => `translateY(${value}px)`),
          }}
        />
      </Box>
    </Box>
  );
};

export default DescriptionList;
